/**
 * Editor Theme Panel Component
 * Theme customization (colors and fonts)
 */
import React, { useState, useEffect } from "react";
import { Box, Button as MuiButton, IconButton, Typography, Paper, Tabs, Tab, TextField, Chip } from "@mui/material";
import { Close as CloseIcon, Check as CheckIcon } from "@mui/icons-material";
import { getGoogleFontsUrl } from "../../../config/apiConfig";
import { themePresets, colorSwatches, fontOptions } from "../../../config/themeConstants";

interface ThemePanelProps {
  theme: { primary: string; secondary: string; font: string; fontEN?: string };
  onThemeChange: (theme: { primary: string; secondary: string; font: string; fontEN?: string }) => void;
  onClose: () => void;
}

export const EditorThemePanel: React.FC<ThemePanelProps> = ({ theme, onThemeChange, onClose }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [showPrimaryPicker, setShowPrimaryPicker] = useState(false);
  const [showSecondaryPicker, setShowSecondaryPicker] = useState(false);

  // Load all font options from Google Fonts
  useEffect(() => {
    fontOptions.forEach((font) => {
      const fontNameForUrl = font.name.replace(/ /g, "+");
      const linkId = `google-font-${fontNameForUrl}`;
      if (!document.getElementById(linkId)) {
        const link = document.createElement("link");
        link.id = linkId;
        link.rel = "stylesheet";
        link.href = getGoogleFontsUrl(font.name);
        document.head.appendChild(link);
      }
    });
  }, []);

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        bgcolor: "white",
        zIndex: 20,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header with gradient */}
      <Box
        sx={{
          p: 2,
          color: "white",
          position: "relative",
          overflow: "hidden",
          background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.secondary} 100%)`,
        }}
      >
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              Theme Studio
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              Customize your website appearance
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "white", "&:hover": { bgcolor: "rgba(255,255,255,0.2)" } }}>
            <CloseIcon />
          </IconButton>
        </Box>
        {/* Decorative circles */}
        <Box
          sx={{
            position: "absolute",
            bottom: -16,
            right: -16,
            width: 96,
            height: 96,
            bgcolor: "rgba(255,255,255,0.1)",
            borderRadius: "50%",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: -16,
            left: -16,
            width: 64,
            height: 64,
            bgcolor: "rgba(255,255,255,0.1)",
            borderRadius: "50%",
          }}
        />
      </Box>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        variant="fullWidth"
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label="🎨 Presets" sx={{ textTransform: "none", fontSize: 14 }} />
        <Tab label="🎯 Colors" sx={{ textTransform: "none", fontSize: 14 }} />
        <Tab label="✏️ Fonts" sx={{ textTransform: "none", fontSize: 14 }} />
      </Tabs>

      {/* Tab Content */}
      <Box sx={{ flex: 1, overflow: "auto" }}>
        {/* Presets Tab */}
        {activeTab === 0 && (
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
            {themePresets.map((preset) => {
              const isActive = theme.primary === preset.primary && theme.secondary === preset.secondary;
              return (
                <Paper
                  key={preset.name}
                  onClick={() => onThemeChange({ ...preset })}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    cursor: "pointer",
                    borderRadius: 3,
                    transition: "all 0.2s",
                    border: 2,
                    borderColor: isActive ? "primary.main" : "transparent",
                    bgcolor: isActive ? `${preset.primary}15` : "white",
                    "&:hover": { boxShadow: 2, borderColor: isActive ? "primary.main" : "grey.200" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    {/* Mini Preview Card */}
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        overflow: "hidden",
                        flexShrink: 0,
                        boxShadow: 1,
                        background: `linear-gradient(135deg, ${preset.primary} 0%, ${preset.secondary} 100%)`,
                      }}
                    >
                      <Box sx={{ width: "100%", height: 8, bgcolor: "rgba(255,255,255,0.3)" }} />
                      <Box sx={{ p: 0.75 }}>
                        <Box
                          sx={{ width: "100%", height: 4, bgcolor: "rgba(255,255,255,0.5)", borderRadius: 1, mb: 0.5 }}
                        />
                        <Box sx={{ width: "66%", height: 4, bgcolor: "rgba(255,255,255,0.3)", borderRadius: 1 }} />
                      </Box>
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography sx={{ fontSize: 18 }}>{preset.emoji}</Typography>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {preset.name}
                        </Typography>
                        {isActive && (
                          <Chip
                            label="Active"
                            size="small"
                            color="primary"
                            sx={{ ml: "auto", height: 20, fontSize: 11 }}
                          />
                        )}
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: "50%",
                            border: "2px solid white",
                            boxShadow: 1,
                            bgcolor: preset.primary,
                          }}
                        />
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: "50%",
                            border: "2px solid white",
                            boxShadow: 1,
                            bgcolor: preset.secondary,
                          }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                          {preset.primary.toUpperCase()}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}

        {/* Colors Tab */}
        {activeTab === 1 && (
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Primary Color */}
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Primary Color
                </Typography>
                <Box
                  onClick={() => setShowPrimaryPicker(!showPrimaryPicker)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 2,
                    cursor: "pointer",
                    "&:hover": { bgcolor: "grey.100" },
                  }}
                >
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      boxShadow: `0 0 0 2px ${theme.primary}30, inset 0 2px 4px rgba(0,0,0,0.1)`,
                      border: "2px solid white",
                      bgcolor: theme.primary,
                    }}
                  />
                  <Typography variant="caption" fontFamily="monospace" color="text.secondary">
                    {theme.primary}
                  </Typography>
                </Box>
              </Box>

              {/* Color Swatches */}
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(8, 1fr)", gap: 0.75 }}>
                {colorSwatches.primary.map((color) => (
                  <Box
                    key={color}
                    onClick={() => onThemeChange({ ...theme, primary: color })}
                    sx={{
                      width: "100%",
                      aspectRatio: "1",
                      borderRadius: 2,
                      cursor: "pointer",
                      transition: "transform 0.1s",
                      bgcolor: color,
                      border: theme.primary === color ? "2px solid" : "none",
                      borderColor: "primary.main",
                      boxShadow: theme.primary === color ? 2 : 0,
                      "&:hover": { transform: "scale(1.1)" },
                    }}
                    title={color}
                  />
                ))}
              </Box>

              {/* Custom Color Input */}
              {showPrimaryPicker && (
                <Box sx={{ mt: 1.5, display: "flex", gap: 1 }}>
                  <input
                    type="color"
                    value={theme.primary}
                    onChange={(e) => onThemeChange({ ...theme, primary: e.target.value })}
                    style={{ width: 48, height: 40, borderRadius: 8, cursor: "pointer", border: "none" }}
                  />
                  <TextField
                    size="small"
                    value={theme.primary}
                    onChange={(e) => onThemeChange({ ...theme, primary: e.target.value })}
                    placeholder="#000000"
                    fullWidth
                    sx={{ "& input": { fontFamily: "monospace" } }}
                  />
                </Box>
              )}
            </Box>

            {/* Secondary Color */}
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Secondary Color
                </Typography>
                <Box
                  onClick={() => setShowSecondaryPicker(!showSecondaryPicker)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 2,
                    cursor: "pointer",
                    "&:hover": { bgcolor: "grey.100" },
                  }}
                >
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      boxShadow: `0 0 0 2px ${theme.secondary}30, inset 0 2px 4px rgba(0,0,0,0.1)`,
                      border: "2px solid white",
                      bgcolor: theme.secondary,
                    }}
                  />
                  <Typography variant="caption" fontFamily="monospace" color="text.secondary">
                    {theme.secondary}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 0.75 }}>
                {colorSwatches.secondary.map((color) => (
                  <Box
                    key={color}
                    onClick={() => onThemeChange({ ...theme, secondary: color })}
                    sx={{
                      width: "100%",
                      aspectRatio: "1",
                      borderRadius: 2,
                      cursor: "pointer",
                      transition: "transform 0.1s",
                      bgcolor: color,
                      border: theme.secondary === color ? "2px solid" : "none",
                      borderColor: "primary.main",
                      boxShadow: theme.secondary === color ? 2 : 0,
                      "&:hover": { transform: "scale(1.1)" },
                    }}
                    title={color}
                  />
                ))}
              </Box>

              {showSecondaryPicker && (
                <Box sx={{ mt: 1.5, display: "flex", gap: 1 }}>
                  <input
                    type="color"
                    value={theme.secondary}
                    onChange={(e) => onThemeChange({ ...theme, secondary: e.target.value })}
                    style={{ width: 48, height: 40, borderRadius: 8, cursor: "pointer", border: "none" }}
                  />
                  <TextField
                    size="small"
                    value={theme.secondary}
                    onChange={(e) => onThemeChange({ ...theme, secondary: e.target.value })}
                    placeholder="#000000"
                    fullWidth
                    sx={{ "& input": { fontFamily: "monospace" } }}
                  />
                </Box>
              )}
            </Box>

            {/* Live Preview */}
            <Paper elevation={0} sx={{ borderRadius: 3, overflow: "hidden", border: 1, borderColor: "divider" }}>
              <Box
                sx={{
                  p: 2,
                  color: "white",
                  background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.secondary} 100%)`,
                }}
              >
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Live Preview
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  See your colors in action
                </Typography>
              </Box>
              <Box sx={{ p: 2, bgcolor: "grey.50", display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <MuiButton
                    size="small"
                    variant="contained"
                    sx={{ bgcolor: theme.primary, textTransform: "none", fontSize: 13 }}
                  >
                    Primary Button
                  </MuiButton>
                  <MuiButton
                    size="small"
                    variant="contained"
                    sx={{ bgcolor: theme.secondary, textTransform: "none", fontSize: 13 }}
                  >
                    Secondary
                  </MuiButton>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: theme.primary }} />
                  <Typography variant="body2" sx={{ color: theme.primary }}>
                    Primary text color
                  </Typography>
                </Box>
                <Box
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    background: `linear-gradient(90deg, ${theme.primary} 0%, ${theme.secondary} 100%)`,
                  }}
                />
              </Box>
            </Paper>
          </Box>
        )}

        {/* Fonts Tab */}
        {activeTab === 2 && (
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Thai Font */}
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 1 }}>
              Font ภาษาไทย
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {fontOptions.map((font) => (
                <Paper
                  key={font.name}
                  onClick={() => onThemeChange({ ...theme, font: font.name })}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    cursor: "pointer",
                    borderRadius: 2,
                    transition: "all 0.2s",
                    border: 2,
                    borderColor: theme.font === font.name ? "primary.main" : "grey.100",
                    bgcolor: theme.font === font.name ? "primary.light" : "white",
                    "&:hover": { bgcolor: theme.font === font.name ? "primary.light" : "grey.50" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>{font.label}</Typography>
                      <Typography sx={{ fontSize: 18, fontFamily: `'${font.name}', sans-serif` }}>{font.sample}</Typography>
                    </Box>
                    {theme.font === font.name && (
                      <Box sx={{ width: 20, height: 20, bgcolor: "primary.main", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <CheckIcon sx={{ fontSize: 12, color: "white" }} />
                      </Box>
                    )}
                  </Box>
                </Paper>
              ))}
            </Box>

            {/* English Font */}
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 1, mt: 1 }}>
              Font English
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {fontOptions.map((font) => (
                <Paper
                  key={`en-${font.name}`}
                  onClick={() => onThemeChange({ ...theme, fontEN: font.name })}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    cursor: "pointer",
                    borderRadius: 2,
                    transition: "all 0.2s",
                    border: 2,
                    borderColor: (theme.fontEN || theme.font) === font.name ? "primary.main" : "grey.100",
                    bgcolor: (theme.fontEN || theme.font) === font.name ? "primary.light" : "white",
                    "&:hover": { bgcolor: (theme.fontEN || theme.font) === font.name ? "primary.light" : "grey.50" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>{font.label}</Typography>
                      <Typography sx={{ fontSize: 18, fontFamily: `'${font.name}', sans-serif` }}>The Quick Brown Fox</Typography>
                    </Box>
                    {(theme.fontEN || theme.font) === font.name && (
                      <Box sx={{ width: 20, height: 20, bgcolor: "primary.main", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <CheckIcon sx={{ fontSize: 12, color: "white" }} />
                      </Box>
                    )}
                  </Box>
                </Paper>
              ))}
            </Box>

            {/* Font Preview */}
            <Paper elevation={0} sx={{ mt: 1, p: 2, bgcolor: "grey.50", borderRadius: 3 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>Preview</Typography>
              <Typography sx={{ fontSize: 20, fontFamily: `'${theme.font}', sans-serif`, color: theme.primary, fontWeight: 700, mb: 0.5 }}>
                ยินดีต้อนรับสู่คลินิกของเรา
              </Typography>
              <Typography sx={{ fontSize: 20, fontFamily: `'${theme.fontEN || theme.font}', sans-serif`, color: theme.primary, fontWeight: 700 }}>
                Welcome to Our Clinic
              </Typography>
            </Paper>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default EditorThemePanel;
